import { useEffect, useState } from "react";
import API from "../../api/api";
import Modal from "../../components/Modal";
import Toast from "../../components/Toast";

const IMG_BASE = "https://cmsapi.mks.org.au/uploads/";

function CommitteeList() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        memberId: null,
        name: "",
        designation: "",
        contact: "",
        bio: "",
        startYear: new Date().getFullYear(),
        endYear: 0,
        image: null
    });
    const [toast, setToast] = useState(null);

    const fetchList = () => {
        setLoading(true);
        console.log("Fetching committee list from /committee...");
        API.get("/committee")
            .then(res => {
                console.log("Committee list received:", res.data);
                setList(res.data);
            })
            .catch(err => {
                console.error("Error fetching committee list:", err);
                console.error("Error details:", err.response?.data || err.message);
                setList([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleOpenModal = (member = null) => {
        if (member) {
            setEditMode(true);
            setFormData({
                memberId: member.MemberId,
                name: member.Name,
                designation: member.Designation,
                contact: member.Contact,
                bio: member.Bio,
                startYear: member.TenureStartYear,
                endYear: member.TenureEndYear,
                image: null // resetting image on edit unless changed
            });
        } else {
            setEditMode(false);
            setFormData({
                memberId: null,
                name: "",
                designation: "",
                contact: "",
                bio: "",
                startYear: new Date().getFullYear(),
                endYear: 0,
                image: null
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            if (formData.image) data.append("image", formData.image);

            // The API seems to prefer query params for some fields based on Swagger, 
            // but usually multipart body is better. Let's try sending as query params in the URL if it fails.
            // Swagger says: POST /api/committee?name=...&designation=...

            const params = new URLSearchParams({
                name: formData.name,
                designation: formData.designation,
                bio: formData.bio,
                contact: formData.contact,
                startYear: formData.startYear,
                endYear: formData.endYear
            }).toString();

            if (editMode) {
                await API.put(`/committee/${formData.memberId}?${params}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                showToast("Member updated successfully");
            } else {
                await API.post(`/committee?${params}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                showToast("Member added successfully");
            }
            setIsModalOpen(false);
            fetchList();
        } catch (err) {
            showToast("Error saving member", "error");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this member?")) {
            try {
                await API.delete(`/committee/${id}`);
                showToast("Member deleted successfully");
                fetchList();
            } catch (err) {
                showToast("Error deleting member", "error");
            }
        }
    };

    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    return (
        <div className="admin-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h2 style={{ margin: 0 }}>Committee Members</h2>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>+ Add Member</button>
            </div>

            {loading ? (
                <p>Loading members...</p>
            ) : (
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Photo</th>
                            <th>Name</th>
                            <th>Designation</th>
                            <th>Contact</th>
                            <th>Tenure</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map(x => (
                            <tr key={x.MemberId}>
                                <td>
                                    <img
                                        src={x.PhotoFile ? (x.PhotoFile.startsWith('http') ? x.PhotoFile : IMG_BASE + x.PhotoFile) : `https://via.assets.so/img.jpg?w=40&h=40&tc=white&bg=#ccd1d9&t=${x.Name ? x.Name.charAt(0) : 'M'}`}
                                        className="member-photo"
                                        alt={x.Name}
                                    />
                                </td>
                                <td><strong>{x.Name}</strong></td>
                                <td>{x.Designation}</td>
                                <td>{x.Contact}</td>
                                <td>{x.TenureStartYear} - {x.TenureEndYear === 0 ? "Present" : x.TenureEndYear}</td>
                                <td>
                                    <button className="btn btn-outline" onClick={() => handleOpenModal(x)} style={{ padding: "4px 8px", marginRight: "8px" }}>Edit</button>
                                    <button className="btn btn-outline" onClick={() => handleDelete(x.MemberId)} style={{ padding: "4px 8px", color: "var(--danger)" }}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editMode ? "Edit Member" : "Add New Member"}
            >
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Name</label>
                        <input className="form-control" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                        <label>Designation</label>
                        <input className="form-control" value={formData.designation} onChange={e => setFormData({ ...formData, designation: e.target.value })} required />
                    </div>
                    <div className="form-group">
                        <label>Contact</label>
                        <input className="form-control" value={formData.contact} onChange={e => setFormData({ ...formData, contact: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Bio</label>
                        <textarea className="form-control" rows="3" value={formData.bio} onChange={e => setFormData({ ...formData, bio: e.target.value })} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                        <div className="form-group">
                            <label>Start Year</label>
                            <input type="number" className="form-control" value={formData.startYear} onChange={e => setFormData({ ...formData, startYear: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label>End Year (0 for Present)</label>
                            <input type="number" className="form-control" value={formData.endYear} onChange={e => setFormData({ ...formData, endYear: e.target.value })} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Photo</label>
                        <input type="file" className="form-control" onChange={e => setFormData({ ...formData, image: e.target.files[0] })} />
                    </div>
                    <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
                        <button type="button" className="btn btn-outline" onClick={() => setIsModalOpen(false)} style={{ marginRight: "10px" }}>Cancel</button>
                        <button type="submit" className="btn btn-primary">{editMode ? "Update Member" : "Add Member"}</button>
                    </div>
                </form>
            </Modal>

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
}

export default CommitteeList;